import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Banner,
  BaseButton,
  Checkbox,
  POLICY_URLS,
  SCREENS,
  Toast,
  useMediaQuery,
  useT,
} from '@repo/common';
import { ReactComponent as WarningIcon } from '@repo/common/assets/icons/20px/icon_20px_warning_filled.svg';
import {
  createJobPost,
  getJobPostDetail,
  getJobPostLogoUploadUrl,
  getJobPostOptions,
  uploadJobPostLogoToS3,
  updateJobPost,
} from '../../../api/jobboard/jobboard.api';

import CompanyLogoField from './CompanyLogoField';
import JobInfo from './JobInfo/JobInfo';
import VisaAndEligibility from './VisaAndEligibility/VisaAndEligibility';
import Conditions from './Conditions/Conditions';
import Requirements from './Requirements/Requirements';
import Details from './Details/Details';
import ApplicationDeadline from './ApplicationDeadline/ApplicationDeadline';
import PerksAndBenefits from './PerksAndBenefits/PerksAndBenefits';
import JobPostMobileWizard from './JobPostMobileWizard';
import { validateAll } from './jobPostValidation';

const EMPTY_OPTIONS = [];
const EMPTY_COMPANY_LOGO = {
  persistedUrl: '',
  previewUrl: '',
  pendingFile: null,
  error: '',
};

const getThreeWeeksLater = () => {
  const date = new Date();
  date.setDate(date.getDate() + 21);
  return date.toISOString().slice(0, 10);
};

const parseOptionalLong = (value) => {
  if (!value) return null;
  const numeric = Number(String(value).replaceAll(',', '').trim());
  return Number.isFinite(numeric) ? numeric : null;
};

const toHiringStatus = (value) =>
  value === 'ongoing' ? 'ONGOING_HIRING' : 'ACTIVE_HIRING';

const normalize = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const findOptionItemIdByLabel = (options = [], label, aliases = []) => {
  if (!label && aliases.length === 0) return null;

  const targets = [label, ...aliases].map(normalize).filter(Boolean);
  const matched = options.find((item) =>
    targets.includes(normalize(item.optionLabel)),
  );
  return matched?.id ?? null;
};

const findOptionItemIdByCode = (options = [], optionCode) => {
  if (!optionCode) return null;
  const matched = options.find((item) => item.optionCode === optionCode);
  return matched?.id ?? null;
};

const getOptionItemById = (options = [], id) =>
  options.find((item) => item.id === id) ?? null;

const mapTopikCodeToValue = (optionCode) => {
  switch (optionCode) {
    case 'PREFERRED':
      return 'preferred';
    case 'REQUIRED':
      return 'required';
    case 'MIN_LEVEL':
      return 'required with minimum level';
    case 'NOT_REQUIRED':
    default:
      return 'not required';
  }
};

const createInitialTextValues = () => ({
  company: '',
  companyLogoUrl: '',
  workLocation: '',
  companyAddress: '',
  jobTitle: '',
  role: '',
  customRole: '',
  employmentType: '',

  visaTypes: [],
  visaSponsorship: 'yes',
  nationality: '',

  workingSchedule: '',
  officeDays: '',
  workTime: '',
  workingDays: '',
  salaryType: '',
  currency: 'Not specified',
  minAmount: '',
  maxAmount: '',

  koreaResidentsOnly: false,
  koreanSkills: '',
  topikRequirement: 'not required',
  topikRequirementLevel: '',
  topikSpeakingRequirement: 'not required',
  topikSpeakingRequirementLevel: '',
  applicantNationality: [],
  noNationalitySelection: false,
  careerLevel: '',
  requiredLanguage: [],
  noRequiredLanguageSelection: false,
  portfolioSubmission: false,
  resumeRequired: false,

  aboutRole: '',
  jobDescription: '',

  hiringStatus: 'ongoing',
  deadline: getThreeWeeksLater(),
  startAvailability: '',
  responseSpeed: '',
  perksAndBenefits: '',
});

const JobPostForm = forwardRef((_props, ref) => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(`(max-width: ${SCREENS.tabletH - 0.02}px)`);
  const [successToast, setSuccessToast] = useState('');
  const [failedValidationField, setFailedValidationField] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const clearFailedValidationField = useCallback(
    () => setFailedValidationField(null),
    [],
  );
  const { jobId } = useParams();
  const isEditMode = Boolean(jobId);
  const [optionGroups, setOptionGroups] = useState([]);
  const [textValues, setTextValues] = useState(createInitialTextValues);
  const [companyLogo, setCompanyLogo] = useState(EMPTY_COMPANY_LOGO);
  const [editMeta, setEditMeta] = useState({ status: null, publishedAt: null });
  const [isInitializing, setIsInitializing] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPostingAgreementChecked, setIsPostingAgreementChecked] =
    useState(false);
  const [showPostingAgreementError, setShowPostingAgreementError] =
    useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchOptionGroups = async () => {
      try {
        const groups = await getJobPostOptions();
        if (isMounted) {
          setOptionGroups(groups || []);
        }
      } catch (error) {
        console.error('Failed to fetch job post options:', error);
      }
    };

    fetchOptionGroups();

    return () => {
      isMounted = false;
    };
  }, []);

  const optionsByGroupKey = useMemo(() => {
    const grouped = {};
    (optionGroups || []).forEach((group) => {
      grouped[group.groupKey] = group.items || [];
    });
    return grouped;
  }, [optionGroups]);

  const roleOptions = optionsByGroupKey.ROLE ?? EMPTY_OPTIONS;
  const employmentTypeOptions =
    optionsByGroupKey.EMPLOYMENT_TYPE ?? EMPTY_OPTIONS;
  const visaTypeOptions = optionsByGroupKey.ELIGIBLE_VISA_TYPE ?? EMPTY_OPTIONS;
  const workingScheduleOptions =
    optionsByGroupKey.WORKING_SCHEDULE ?? EMPTY_OPTIONS;
  const workingScheduleDetailOptions =
    optionsByGroupKey.WORKING_SCHEDULE_DETAIL ?? EMPTY_OPTIONS;
  const salaryTypeOptions = optionsByGroupKey.SALARY_TYPE ?? EMPTY_OPTIONS;
  const currencyOptions = optionsByGroupKey.CURRENCY ?? EMPTY_OPTIONS;
  const koreanSkillLevelOptions =
    optionsByGroupKey.KOREAN_SKILLS_LEVEL ?? EMPTY_OPTIONS;
  const topikRequirementOptions =
    optionsByGroupKey.TOPIK_REQUIREMENT ?? EMPTY_OPTIONS;
  const topikSpeakingRequirementOptions =
    optionsByGroupKey.TOPIK_SPEAKING_REQUIREMENT ?? EMPTY_OPTIONS;
  const topikLevelOptions = optionsByGroupKey.TOPIK_LEVEL ?? EMPTY_OPTIONS;
  const nationalityOptions = optionsByGroupKey.NATIONALITY ?? EMPTY_OPTIONS;
  const careerLevelOptions = optionsByGroupKey.CAREER_LEVEL ?? EMPTY_OPTIONS;
  const startAvailabilityOptions =
    optionsByGroupKey.START_AVAILABILITY ?? EMPTY_OPTIONS;
  const responseSpeedOptions =
    optionsByGroupKey.RESPONSE_SPEED ?? EMPTY_OPTIONS;
  const requiredLanguageOptions =
    optionsByGroupKey.REQUIRED_LANGUAGE ?? EMPTY_OPTIONS;

  useEffect(() => {
    if (!isEditMode || !optionGroups.length) return;

    let isMounted = true;

    const hydrateEditForm = async () => {
      try {
        setIsInitializing(true);
        const detail = await getJobPostDetail(jobId);
        if (!isMounted || !detail) return;

        const roleItems = optionsByGroupKey.ROLE ?? EMPTY_OPTIONS;
        const employmentTypeItems =
          optionsByGroupKey.EMPLOYMENT_TYPE ?? EMPTY_OPTIONS;
        const visaTypeItems =
          optionsByGroupKey.ELIGIBLE_VISA_TYPE ?? EMPTY_OPTIONS;
        const workingScheduleItems =
          optionsByGroupKey.WORKING_SCHEDULE ?? EMPTY_OPTIONS;
        const workingScheduleDetailItems =
          optionsByGroupKey.WORKING_SCHEDULE_DETAIL ?? EMPTY_OPTIONS;
        const salaryTypeItems = optionsByGroupKey.SALARY_TYPE ?? EMPTY_OPTIONS;
        const currencyItems = optionsByGroupKey.CURRENCY ?? EMPTY_OPTIONS;
        const koreanSkillLevelItems =
          optionsByGroupKey.KOREAN_SKILLS_LEVEL ?? EMPTY_OPTIONS;
        const topikRequirementItems =
          optionsByGroupKey.TOPIK_REQUIREMENT ?? EMPTY_OPTIONS;
        const topikSpeakingRequirementItems =
          optionsByGroupKey.TOPIK_SPEAKING_REQUIREMENT ?? EMPTY_OPTIONS;
        const topikLevelItems = optionsByGroupKey.TOPIK_LEVEL ?? EMPTY_OPTIONS;
        const nationalityItems = optionsByGroupKey.NATIONALITY ?? EMPTY_OPTIONS;
        const careerLevelItems =
          optionsByGroupKey.CAREER_LEVEL ?? EMPTY_OPTIONS;
        const startAvailabilityItems =
          optionsByGroupKey.START_AVAILABILITY ?? EMPTY_OPTIONS;
        const responseSpeedItems =
          optionsByGroupKey.RESPONSE_SPEED ?? EMPTY_OPTIONS;
        const requiredLanguageItems =
          optionsByGroupKey.REQUIRED_LANGUAGE ?? EMPTY_OPTIONS;

        const roleItem = getOptionItemById(roleItems, detail.roleOptionItemId);
        const employmentTypeItem = getOptionItemById(
          employmentTypeItems,
          detail.employmentTypeOptionItemId,
        );
        const selectedVisaTypes = (detail.visaTypeOptionItemIds || [])
          .map((optionItemId) => getOptionItemById(visaTypeItems, optionItemId))
          .map((item) => item?.optionLabel)
          .filter(Boolean);
        const workingScheduleItem = getOptionItemById(
          workingScheduleItems,
          detail.workingScheduleOptionItemId,
        );
        const officeDaysItem = getOptionItemById(
          workingScheduleDetailItems,
          detail.workingScheduleDetailOptionItemId,
        );
        const salaryTypeItem = getOptionItemById(
          salaryTypeItems,
          detail.salaryTypeOptionItemId,
        );
        const currencyItem = getOptionItemById(
          currencyItems,
          detail.currencyOptionItemId,
        );
        const koreanSkillItem = getOptionItemById(
          koreanSkillLevelItems,
          detail.koreanSkillLevelOptionItemId,
        );
        const topikRequirementItem = getOptionItemById(
          topikRequirementItems,
          detail.topikRequirementOptionItemId,
        );
        const topikRequirementLevelItem = getOptionItemById(
          topikLevelItems,
          detail.topikRequirementLevelOptionItemId,
        );
        const topikSpeakingRequirementItem = getOptionItemById(
          topikSpeakingRequirementItems,
          detail.topikSpeakingRequirementOptionItemId,
        );
        const topikSpeakingLevelItem = getOptionItemById(
          topikLevelItems,
          detail.topikSpeakingLevelOptionItemId,
        );
        const nationalityItem = getOptionItemById(
          nationalityItems,
          detail.nationalityOptionItemId,
        );
        const careerLevelItem = getOptionItemById(
          careerLevelItems,
          detail.careerLevelOptionItemId,
        );
        const startAvailabilityItem = getOptionItemById(
          startAvailabilityItems,
          detail.startAvailabilityOptionItemId,
        );
        const responseSpeedItem = getOptionItemById(
          responseSpeedItems,
          detail.responseSpeedOptionItemId,
        );

        const requiredLanguageValues = (detail.requiredLanguages || [])
          .map((language) => {
            const item = getOptionItemById(
              requiredLanguageItems,
              language?.optionItemId,
            );
            if (!item) return null;
            if (item.optionCode === 'OTHER') {
              return language?.customText || item.optionLabel;
            }
            return item.optionLabel?.toLowerCase?.() || null;
          })
          .filter(Boolean);

        const mappedNationality =
          detail.nationalityCustomText ||
          (nationalityItem?.optionCode === 'OTHER'
            ? ''
            : nationalityItem?.optionLabel || '');

        const nextValues = {
          ...createInitialTextValues(),
          company: detail.companyName || '',
          companyLogoUrl: detail.companyLogoUrl || '',
          workLocation: detail.location || '',
          companyAddress:
            detail.companyAddressDetail || detail.companyAddress || '',
          jobTitle: detail.title || '',
          role: roleItem?.optionLabel || '',
          customRole: detail.roleCustomText || '',
          employmentType: employmentTypeItem?.optionLabel || '',
          visaTypes: selectedVisaTypes,
          visaSponsorship: detail.visaSponsorship ? 'yes' : 'no',
          nationality: mappedNationality,
          workingSchedule: workingScheduleItem?.optionLabel || '',
          officeDays: officeDaysItem?.optionLabel || '',
          workTime: detail.workTime || '',
          workingDays: detail.workingDays || '',
          salaryType: salaryTypeItem?.optionLabel || '',
          currency: currencyItem?.optionLabel || 'Not specified',
          minAmount:
            detail.amountMin !== null && detail.amountMin !== undefined
              ? String(detail.amountMin)
              : '',
          maxAmount:
            detail.amountMax !== null && detail.amountMax !== undefined
              ? String(detail.amountMax)
              : '',
          koreaResidentsOnly: Boolean(detail.koreaResidentsOnly),
          koreanSkills: koreanSkillItem?.optionLabel || '',
          topikRequirement: mapTopikCodeToValue(
            topikRequirementItem?.optionCode,
          ),
          topikRequirementLevel: topikRequirementLevelItem?.optionLabel || '',
          topikSpeakingRequirement: mapTopikCodeToValue(
            topikSpeakingRequirementItem?.optionCode,
          ),
          topikSpeakingRequirementLevel:
            topikSpeakingLevelItem?.optionLabel || '',
          applicantNationality: mappedNationality ? [mappedNationality] : [],
          careerLevel: careerLevelItem?.optionLabel || '',
          requiredLanguage: requiredLanguageValues,
          portfolioSubmission: Boolean(detail.portfolioSubmission),
          resumeRequired: Boolean(detail.resumeRequired),
          aboutRole: detail.aboutRole || '',
          jobDescription: detail.detailInfo || '',
          hiringStatus:
            detail.hiringStatus === 'ONGOING_HIRING' ? 'ongoing' : 'fixed',
          deadline: detail.applicationDeadline || getThreeWeeksLater(),
          startAvailability:
            detail.startAvailabilityCustomText ||
            startAvailabilityItem?.optionLabel ||
            '',
          responseSpeed: responseSpeedItem?.optionLabel || '',
          perksAndBenefits: detail.perks || detail.benefits || '',
        };

        setTextValues(nextValues);
        setCompanyLogo({
          ...EMPTY_COMPANY_LOGO,
          persistedUrl: detail.companyLogoUrl || '',
        });
        setEditMeta({
          status: detail.status || null,
          publishedAt: detail.publishedAt || null,
        });
      } catch (error) {
        console.error('Failed to load job post for edit:', error);
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    hydrateEditForm();

    return () => {
      isMounted = false;
    };
  }, [isEditMode, jobId, optionGroups.length, optionsByGroupKey]);

  const buildPayload = ({
    status,
    forcePublish = false,
    companyLogoUrl = textValues.companyLogoUrl,
  }) => {
    const roleOptionItemId = findOptionItemIdByLabel(
      roleOptions,
      textValues.role,
    );
    const employmentTypeOptionItemId = findOptionItemIdByLabel(
      employmentTypeOptions,
      textValues.employmentType,
    );
    const careerLevelOptionItemId = findOptionItemIdByLabel(
      careerLevelOptions,
      textValues.careerLevel,
    );
    const visaTypeOptionItemIds = (textValues.visaTypes || [])
      .map((visaType) => findOptionItemIdByLabel(visaTypeOptions, visaType))
      .filter(Boolean);
    const workingScheduleOptionItemId = findOptionItemIdByLabel(
      workingScheduleOptions,
      textValues.workingSchedule,
      ['Flexible (negotiable)'],
    );
    const workingScheduleDetailOptionItemId = findOptionItemIdByLabel(
      workingScheduleDetailOptions,
      textValues.officeDays,
      ['1 day / week', '2-3 days / week', '4 days / week'],
    );
    const salaryTypeOptionItemId = findOptionItemIdByLabel(
      salaryTypeOptions,
      textValues.salaryType,
      ['Company policy'],
    );
    const currencyOptionItemId = findOptionItemIdByLabel(
      currencyOptions,
      textValues.currency,
    );
    const koreanSkillLevelOptionItemId = findOptionItemIdByLabel(
      koreanSkillLevelOptions,
      textValues.koreanSkills,
    );
    const topikRequirementOptionItemId = findOptionItemIdByCode(
      topikRequirementOptions,
      {
        'not required': 'NOT_REQUIRED',
        preferred: 'PREFERRED',
        required: 'REQUIRED',
        'required with minimum level': 'MIN_LEVEL',
      }[normalize(textValues.topikRequirement)],
    );
    const topikRequirementLevelOptionItemId = findOptionItemIdByLabel(
      topikLevelOptions,
      textValues.topikRequirementLevel,
    );
    const topikSpeakingRequirementOptionItemId = findOptionItemIdByCode(
      topikSpeakingRequirementOptions,
      {
        'not required': 'NOT_REQUIRED',
        preferred: 'PREFERRED',
        required: 'REQUIRED',
        'required with minimum level': 'MIN_LEVEL',
      }[normalize(textValues.topikSpeakingRequirement)],
    );
    const topikSpeakingLevelOptionItemId = findOptionItemIdByLabel(
      topikLevelOptions,
      textValues.topikSpeakingRequirementLevel,
    );
    const firstNationality = Array.isArray(textValues.applicantNationality)
      ? textValues.applicantNationality.find((value) =>
          String(value || '').trim(),
        )
      : null;
    const mappedNationalityOptionItemId = findOptionItemIdByLabel(
      nationalityOptions,
      firstNationality,
    );
    const nationalityOtherOptionItemId = findOptionItemIdByCode(
      nationalityOptions,
      'OTHER',
    );
    const nationalityOptionItemId =
      mappedNationalityOptionItemId ||
      (firstNationality ? nationalityOtherOptionItemId : null);
    const nationalityCustomText =
      firstNationality && !mappedNationalityOptionItemId
        ? String(firstNationality).trim()
        : null;
    const startAvailabilityOptionItemId = findOptionItemIdByLabel(
      startAvailabilityOptions,
      textValues.startAvailability,
    );
    const normalizedStartAvailability = normalize(textValues.startAvailability);
    const startAvailabilityCustomText =
      !startAvailabilityOptionItemId &&
      normalizedStartAvailability !== 'select preferred start availability'
        ? textValues.startAvailability?.trim() || null
        : null;
    const responseSpeedOptionItemId = findOptionItemIdByLabel(
      responseSpeedOptions,
      textValues.responseSpeed,
    );

    const requiredLanguageCodeMap = {
      english: 'ENGLISH',
      japanese: 'JAPANESE',
      chinese: 'CHINESE',
      korean: 'KOREAN',
    };
    const requiredLanguageOtherId = findOptionItemIdByCode(
      requiredLanguageOptions,
      'OTHER',
    );
    const requiredLanguages = (textValues.requiredLanguage || [])
      .map((languageValue) => {
        const normalized = normalize(languageValue);
        const mappedCode = requiredLanguageCodeMap[normalized];
        if (mappedCode) {
          const optionItemId = findOptionItemIdByCode(
            requiredLanguageOptions,
            mappedCode,
          );
          return optionItemId ? { optionItemId, customText: null } : null;
        }

        if (!requiredLanguageOtherId || !String(languageValue || '').trim()) {
          return null;
        }

        return {
          optionItemId: requiredLanguageOtherId,
          customText: String(languageValue).trim(),
        };
      })
      .filter(Boolean);

    const fixedDeadline = toDate(textValues.deadline);
    const applicationDeadline =
      textValues.hiringStatus === 'fixed' && fixedDeadline
        ? new Date(
            fixedDeadline.getFullYear(),
            fixedDeadline.getMonth(),
            fixedDeadline.getDate(),
            23,
            59,
            59,
            999,
          ).toISOString()
        : null;
    const nowIso = new Date().toISOString();
    const effectiveStatus =
      status ?? (isEditMode && editMeta?.status ? editMeta.status : 'ACTIVE');
    // 이미 게시된 공고를 수정·재게시해도 최초 게시 시각은 유지한다 (서버도 클라이언트 값을 무시함)
    const effectivePublishedAt =
      effectiveStatus === 'ACTIVE' ? (editMeta?.publishedAt ?? nowIso) : null;

    return {
      title: textValues.jobTitle?.trim(),
      companyName: textValues.company?.trim() || null,
      companyLogoUrl: companyLogoUrl?.trim() || null,
      location: textValues.workLocation?.trim() || null,
      companyAddress: textValues.companyAddress?.trim() || null,
      // 폼에 상세주소 입력란이 따로 없다. 같은 값을 두 번 저장하면 상세 화면에 주소가 두 번 찍힌다
      companyAddressDetail: null,
      roleOptionItemId,
      roleCustomText:
        normalize(textValues.role) === 'other (enter manually)' ||
        normalize(textValues.role) === 'other'
          ? textValues.customRole?.trim() || null
          : null,
      employmentTypeOptionItemId,
      workingScheduleOptionItemId,
      workingScheduleDetailOptionItemId:
        normalize(textValues.workingSchedule) === 'hybrid'
          ? workingScheduleDetailOptionItemId
          : null,
      workTime: textValues.workTime?.trim() || null,
      workingDays: textValues.workingDays?.trim() || null,
      salaryTypeOptionItemId,
      amountMin: parseOptionalLong(textValues.minAmount),
      amountMax: parseOptionalLong(textValues.maxAmount),
      currencyOptionItemId,
      currencyCustomText: null,
      visaSponsorship: textValues.visaSponsorship === 'yes',
      // 해외 거주자 지원 가능 여부는 이 값의 반대 (별도 컬럼 없음)
      koreaResidentsOnly: textValues.koreaResidentsOnly,
      koreanSkillLevelOptionItemId,
      topikRequirementOptionItemId,
      topikRequirementLevelOptionItemId:
        normalize(textValues.topikRequirement) === 'required with minimum level'
          ? topikRequirementLevelOptionItemId
          : null,
      topikSpeakingRequirementOptionItemId,
      topikSpeakingLevelOptionItemId:
        normalize(textValues.topikSpeakingRequirement) ===
        'required with minimum level'
          ? topikSpeakingLevelOptionItemId
          : null,
      nationalityOptionItemId,
      nationalityCustomText,
      careerLevelOptionItemId,
      aboutRole: textValues.aboutRole?.trim() || null,
      detailInfo: textValues.jobDescription?.trim() || null,
      perks: textValues.perksAndBenefits?.trim() || null,
      benefits: null,
      applicationDeadline,
      hiringStatus: toHiringStatus(textValues.hiringStatus),
      startAvailabilityOptionItemId,
      startAvailabilityCustomText,
      portfolioSubmission: textValues.portfolioSubmission,
      resumeRequired: textValues.resumeRequired,
      responseSpeedOptionItemId,
      status: effectiveStatus,
      publishedAt: effectivePublishedAt,
      visaTypeOptionItemIds,
      requiredLanguages,
    };
  };

  const submitJob = async ({
    status,
    forcePublish = false,
    successPath = '/jobs/admin',
  } = {}) => {
    const isPublishing = forcePublish || status === 'ACTIVE';

    if (isPublishing) {
      const nextErrors = validateAll(textValues, t);
      setErrors(nextErrors);

      const firstInvalidField = Object.keys(nextErrors)[0];
      if (firstInvalidField) {
        if (isMobile) {
          setFailedValidationField(firstInvalidField);
          return;
        }

        requestAnimationFrame(() => {
          const field = document.querySelector(
            `[data-validation-field="${firstInvalidField}"]`,
          );
          field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (!field?.readOnly) field?.focus?.({ preventScroll: true });
        });
        return;
      }
    }

    if (isPublishing && !isPostingAgreementChecked) {
      setShowPostingAgreementError(true);
      document.getElementById('job-post-agreement')?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }

    try {
      setSubmitError('');
      setIsSubmitting(true);
      let uploadedCompanyLogoUrl = textValues.companyLogoUrl;

      if (companyLogo.pendingFile) {
        const { uploadUrl, key } = await getJobPostLogoUploadUrl(
          companyLogo.pendingFile.name,
        );
        await uploadJobPostLogoToS3(companyLogo.pendingFile, uploadUrl);
        uploadedCompanyLogoUrl = key;
        setTextValues((prev) => ({
          ...prev,
          companyLogoUrl: key,
        }));
        setCompanyLogo((prev) => ({
          ...prev,
          pendingFile: null,
        }));
      }

      const payload = buildPayload({
        status,
        forcePublish,
        companyLogoUrl: uploadedCompanyLogoUrl,
      });

      // 서버가 제목을 필수로 받는다. 임시저장이어도 제목 없이는 저장할 수 없다
      if (!payload.title) {
        const message = t(
          'Enter a job title to save a draft.',
          '임시저장하려면 공고 제목을 입력해 주세요.',
        );
        setErrors((prev) => ({ ...prev, jobTitle: message }));
        setSubmitError(message);
        if (isMobile) {
          setFailedValidationField('jobTitle');
        } else {
          requestAnimationFrame(() => {
            const field = document.querySelector(
              '[data-validation-field="jobTitle"]',
            );
            field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            field?.focus?.({ preventScroll: true });
          });
        }
        return;
      }

      let savedId = jobId;
      if (isEditMode && jobId) {
        await updateJobPost(jobId, payload);
      } else {
        const created = await createJobPost(payload);
        savedId = created?.id ?? null;
      }

      if (isPublishing) {
        navigate(savedId ? `/jobs/admin/${savedId}` : successPath, {
          state: { toast: { action: 'publish' } },
        });
        return;
      }

      if (!isEditMode && savedId) {
        navigate(`/jobs/admin/${savedId}/edit`, {
          replace: true,
          state: { toast: { action: 'draft' } },
        });
      } else {
        setSuccessToast(t('Draft saved', '임시 저장되었습니다'));
      }
    } catch (error) {
      console.error('Failed to submit job post:', error);
      const status = error?.response?.status;
      setSubmitError(
        status === 403
          ? t(
              "You don't have permission to edit this job post.",
              '이 공고를 수정할 권한이 없습니다.',
            )
          : status === 400
            ? t(
                'Some fields are too long to save. Please shorten them and try again.',
                '입력한 내용이 너무 길어 저장할 수 없습니다. 줄인 뒤 다시 시도해 주세요.',
              )
            : t(
                "Couldn't save. Please try again.",
                '저장하지 못했습니다. 다시 시도해 주세요.',
              ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePublish = () =>
    submitJob({
      status: 'ACTIVE',
      forcePublish: true,
    });

  const handleSaveDraft = () =>
    submitJob({
      status: 'DRAFT',
      forcePublish: false,
    });

  useImperativeHandle(ref, () => ({
    handlePublish,
    handleSaveDraft,
  }));

  const stepNodes = {
    basic: (
      <JobInfo
        textValues={textValues}
        setTextValues={setTextValues}
        errors={errors}
        roleOptions={roleOptions}
        employmentTypeOptions={employmentTypeOptions}
        companyLogoField={
          <CompanyLogoField
            value={companyLogo}
            onChange={setCompanyLogo}
            onRemove={() => {
              setCompanyLogo(EMPTY_COMPANY_LOGO);
              setTextValues((prev) => ({ ...prev, companyLogoUrl: '' }));
            }}
            isDisabled={isSubmitting || isInitializing}
          />
        }
      />
    ),
    visa: (
      <VisaAndEligibility
        textValues={textValues}
        setTextValues={setTextValues}
        errors={errors}
        visaTypeOptions={visaTypeOptions}
      />
    ),
    conditions: (
      <Conditions
        textValues={textValues}
        setTextValues={setTextValues}
        errors={errors}
      />
    ),
    requirements: (
      <Requirements
        textValues={textValues}
        setTextValues={setTextValues}
        errors={errors}
        careerLevelOptions={careerLevelOptions}
      />
    ),
    details: (
      <Details
        textValues={textValues}
        setTextValues={setTextValues}
        errors={errors}
      />
    ),
    deadline: (
      <ApplicationDeadline
        textValues={textValues}
        setTextValues={setTextValues}
        errors={errors}
      />
    ),
    perks: (
      <PerksAndBenefits
        textValues={textValues}
        setTextValues={setTextValues}
        errors={errors}
      />
    ),
  };

  const lineBreak = isMobile ? ' ' : '\n';

  const agreementNode = (
    <div id='job-post-agreement' className='pb-[50px] pt-10'>
      <Banner
        type='warning'
        size={isMobile ? 's' : 'm'}
        hideClose
        gap='gap-3'
        icon={<WarningIcon className='size-5 shrink-0' />}
        label={
          <span className='flex flex-col gap-2 tabletH:gap-4 typo-b12 tabletH:typo-b14 text-text-primary'>
            <span className={isMobile ? '' : 'whitespace-pre-line'}>
              지원자 정보는 채용 목적으로만 사용할 수 있습니다.{lineBreak}
              열람 기간은 합격자 입사일+90일, 불합격자는 채용 절차 종료 시
              종료되며,{lineBreak}
              이후에는 보관하신 서류를 파기해 주세요.
            </span>

            <span className={isMobile ? '' : 'whitespace-pre-line'}>
              허위 공고는 「직업안정법」에 따라 처벌될 수 있습니다.{lineBreak}
              자세한 기준:{' '}
              <a
                href={POLICY_URLS.businessTerms}
                target='_blank'
                rel='noreferrer noopener'
                onClick={(event) => event.stopPropagation()}
                className='underline underline-offset-2 typo-b12Strong tabletH:typo-b14Strong text-text-primary'
              >
                기업회원 이용약관
              </a>{' '}
              제8조·제18조
            </span>
          </span>
        }
        footer={
          <div className='flex flex-col items-start justify-center gap-2'>
            <Checkbox
              checked={isPostingAgreementChecked}
              onChange={(event) => {
                setIsPostingAgreementChecked(event.target.checked);
                setShowPostingAgreementError(false);
              }}
              label={
                <>
                  위 내용을 확인했으며, 공고 내용이 사실과 일치합니다.
                  <span className='text-text-accent'>*</span>
                </>
              }
              className='!h-auto items-center text-text-primary'
              boxClassName='shrink-0'
              labelClassName='typo-b12Strong tabletH:typo-b14Strong text-text-primary'
            />

            {showPostingAgreementError && (
              <span role='alert' className='typo-b12Strong text-text-failed'>
                공고를 게시하려면 위 내용을 확인해 주세요.
              </span>
            )}
          </div>
        }
      />
    </div>
  );

  useEffect(() => {
    if (location.state?.toast?.action !== 'draft') return;
    setSuccessToast(t('Draft saved', '임시 저장되었습니다'));
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const submitErrorToast = submitError ? (
    <div className='fixed bottom-6 left-0 right-0 z-[1000] flex justify-center px-4'>
      <Toast
        type='error'
        state='solid'
        title={submitError}
        hierarchy='small'
        direction='up'
        customWidth='auto'
        onClose={() => setSubmitError('')}
      />
    </div>
  ) : successToast ? (
    <div className='fixed bottom-6 left-0 right-0 z-[1000] flex justify-center px-4'>
      <Toast
        type='success'
        state='white'
        title={successToast}
        direction='up'
        customWidth='auto'
        onClose={() => setSuccessToast('')}
      />
    </div>
  ) : null;

  const isBusy = isSubmitting || isInitializing;

  if (isMobile) {
    return (
      <JobPostMobileWizard
        isEditMode={isEditMode}
        stepNodes={stepNodes}
        agreement={agreementNode}
        textValues={textValues}
        errors={errors}
        setErrors={setErrors}
        failedValidationField={failedValidationField}
        onFailedFieldHandled={clearFailedValidationField}
        canPublish={isPostingAgreementChecked}
        isBusy={isBusy}
        onSaveDraft={handleSaveDraft}
        onPublish={handlePublish}
        toast={submitErrorToast}
      />
    );
  }

  return (
    <div className='w-full max-w-[719px]'>
      <div className='w-full pb-10'>
        <h1 className='typo-title1 text-text-primary'>
          {isEditMode
            ? t('Edit Job', '공고 수정')
            : t('Create Job', '공고 등록')}
        </h1>
      </div>

      <div className='border border-b border-grayscale-gray02' />

      {stepNodes.basic}
      {stepNodes.visa}
      {stepNodes.conditions}
      {stepNodes.requirements}
      {stepNodes.details}
      {stepNodes.deadline}
      {stepNodes.perks}

      {agreementNode}

      <div className='flex justify-end w-full'>
        <BaseButton
          type='primary'
          size='l'
          hideIcon
          width='120px'
          isDisabled={!isPostingAgreementChecked || isBusy}
          onClick={handlePublish}
        >
          {t('Publish Job', '공고 게시')}
        </BaseButton>
      </div>

      {submitErrorToast}
    </div>
  );
});

export default JobPostForm;
